import {Modal, TextInput} from '@mantine/core';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {zodResolver} from '@hookform/resolvers/zod';
import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {DialogButton} from 'src/design/molecules/DialogButton';
import {SubmitButton} from 'src/design/molecules/SubmitButton';
import {usePromoteSuperAdminMutation} from 'src/redux/queries/userInstance';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {z} from 'zod';

const nameField = z
  .string()
  .max(100, 'Must be 100 characters or fewer.')
  .regex(
    /^[a-zA-Z0-9 \-']*$/,
    "May only contain letters, numbers, spaces, hyphens, and apostrophes."
  );

const schema = z.object({
  email: z.string().trim().email('Please enter a valid email address.'),
  firstName: nameField.or(z.literal('')),
  lastName: nameField.or(z.literal('')),
});

type PromoteSuperAdminFormValues = z.infer<typeof schema>;

const defaultValues: PromoteSuperAdminFormValues = {
  email: '',
  firstName: '',
  lastName: '',
};

export interface PromoteSuperAdminModalProps {
  opened: boolean;
  onClose: () => void;
  onPromoted?: () => void;
}

export const PromoteSuperAdminModal: React.FC<PromoteSuperAdminModalProps> = ({
  opened,
  onClose,
  onPromoted,
}) => {
  const {setAlert} = useAlert();
  const [promoteSuperAdmin, {isLoading}] = usePromoteSuperAdminMutation();

  const {
    control,
    handleSubmit: handleFormSubmit,
    reset,
  } = useForm<PromoteSuperAdminFormValues>({
    mode: 'onSubmit',
    defaultValues,
    resolver: zodResolver(schema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (values: PromoteSuperAdminFormValues) => {
    try {
      await promoteSuperAdmin({
        email: values.email.trim(),
        firstName: values.firstName.trim() || undefined,
        lastName: values.lastName.trim() || undefined,
      }).unwrap();
      setAlert(
        `${values.email.trim()} has been promoted to super admin.`,
        'success'
      );
      reset();
      onPromoted?.();
      onClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Promote to Super Admin"
      size="md"
      styles={{title: {fontWeight: 700}}}>
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <Stack gap="md">
          <Controller
            name="email"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Email"
                placeholder="user@example.com"
                required
                {...field}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="firstName"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="First Name"
                placeholder="First name (optional)"
                {...field}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Last Name"
                placeholder="Last name (optional)"
                {...field}
                error={fieldState.error?.message}
              />
            )}
          />
          <Divider />
          <Group justify="flex-end">
            <DialogButton size="sm" onClick={handleClose}>
              Cancel
            </DialogButton>
            <SubmitButton size="sm" color="yellow" loading={isLoading}>
              Promote
            </SubmitButton>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
